import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal, ModalContent } from '../ui/Modal';
import { Button, Input, Select } from '../ui';
import { formatPhoneKEForInput, normalizePhoneKE } from '../../utils/formatters';
import { Mail, Phone, User, Loader2, X } from 'lucide-react';
import { apiService } from '../../services/api';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: {
    id: string;
    email: string;
    phone: string | null;
    role: string;
    accountStatus: string;
  } | null;
  currentUserRole: string;
  viewOnly?: boolean;
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  overflow: visible;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  overflow: visible;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.foreground};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Required = styled.span`
  color: ${props => props.theme.destructive || '#ef4444'};
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.destructive || '#ef4444'};
  font-size: 14px;
  margin-top: 4px;
`;

const SuccessMessage = styled.div`
  color: ${props => props.theme.success || '#10b981'};
  font-size: 14px;
  margin-top: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const RoleDescription = styled.div`
  font-size: 12px;
  color: ${props => props.theme.mutedForeground};
  margin-top: 4px;
  padding: 8px;
  background: ${props => props.theme.muted}30;
  border-radius: 6px;
`;

const ReadOnlyField = styled.div`
  padding: 10px 12px;
  background: ${props => props.theme.muted}30;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  font-size: 14px;
  color: ${props => props.theme.mutedForeground};
`;

const getRoleDescription = (role: string): string => {
  switch (role) {
    case 'ADMIN':
      return 'Full access to all features. Can manage properties, tenants, payments, agents, and caretakers.';
    case 'AGENT':
      return 'Limited access to assigned properties. Can manage tenants and payments for assigned properties.';
    case 'CARETAKER':
      return 'Read-only access to assigned properties. Can view maintenance requests and property information.';
    default:
      return '';
  }
};

export const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  user,
  currentUserRole,
  viewOnly = false
}) => {
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Determine available roles based on PMS role (OWNER maps to LANDLORD)
  const availableRoles = currentUserRole === 'LANDLORD' || currentUserRole === 'OWNER'
    ? [
        { value: 'ADMIN', label: 'Admin' },
        { value: 'AGENT', label: 'Agent' },
        { value: 'CARETAKER', label: 'Caretaker' }
      ]
    : [
        { value: 'AGENT', label: 'Agent' },
        { value: 'CARETAKER', label: 'Caretaker' }
      ];

  // Initialize form when user changes
  useEffect(() => {
    if (user) {
      setPhone(user.phone || '');
      setRole(user.role);
      setError(null);
      setSuccess(null);
    }
  }, [user]);

  const handleClose = () => {
    if (!loading) {
      setPhone('');
      setRole('');
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhoneKEForInput(e.target.value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    // Prevent double submission
    if (loading) {
      return;
    }
    
    setError(null);
    setSuccess(null);

    if (!phone || !role) {
      setError('Phone and role are required');
      return;
    }

    // Basic phone validation (should be at least 9 digits)
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 9) {
      setError('Please enter a valid phone number');
      return;
    }

    setLoading(true);

    try {
      await apiService.updateTeamUser(user.id, {
        phone: phone.trim(),
        role
      });

      setSuccess('User updated successfully');
      
      // Call onSuccess callback after a short delay to refresh users list
      setTimeout(() => {
        onSuccess();
        handleClose();
        setSuccess(null);
      }, 1500);
    } catch (err: any) {
      console.error('Error updating user:', err);
      const message = err?.response?.data?.error || err?.message || 'Failed to update user. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={viewOnly ? "View User" : "Edit User"} size="md">
      <ModalContent>
        <FormContainer>
          <FormGroup>
            <Label>
              <Mail size={16} />
              Email
            </Label>
            <ReadOnlyField>{user.email}</ReadOnlyField>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
              Email cannot be changed
            </div>
          </FormGroup>

          <FormGroup>
            <Label>
              <Phone size={16} />
              Phone {!viewOnly && <Required>*</Required>}
            </Label>
            {viewOnly ? (
              <ReadOnlyField>{phone || 'Not provided'}</ReadOnlyField>
            ) : (
              <Input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="+254 700 000 000"
                disabled={loading}
              />
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              <User size={16} />
              Role {!viewOnly && <Required>*</Required>}
            </Label>
            {viewOnly ? (
              <ReadOnlyField>{role}</ReadOnlyField>
            ) : (
              <>
                <Select
                  value={role}
                  onChange={(value) => setRole(value as string)}
                  options={availableRoles}
                  placeholder="Select a role"
                  disabled={loading}
                  fullWidth
                />
                {role && (
                  <RoleDescription>
                    {getRoleDescription(role)}
                  </RoleDescription>
                )}
              </>
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              Account Status
            </Label>
            <ReadOnlyField>{user.accountStatus}</ReadOnlyField>
          </FormGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <ButtonGroup>
            {viewOnly ? (
              <Button
                variant="primary"
                onClick={handleClose}
              >
                Close
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={loading || !phone || !role}
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} style={{ marginRight: 8, animation: 'spin 1s linear infinite' }} />
                      Updating...
                    </>
                  ) : (
                    'Update User'
                  )}
                </Button>
              </>
            )}
          </ButtonGroup>
        </FormContainer>
      </ModalContent>
    </Modal>
  );
};
